import type { FindingsPrView } from '../findings-url';
import type { IndexedPullRequest, PrDecisionRow } from '../findings-types';

export type PrInboxView = 'open' | 'merged' | 'closed';
export type PrGate = 'ready' | 'blocked' | 'review' | 'closed' | 'merged';
export type PrCheckDot = 'pass' | 'warn' | 'fail' | 'muted';

export const PR_INBOX_TABS: Array<{ label: string; value: PrInboxView }> = [
  { label: 'Open', value: 'open' },
  { label: 'Merged', value: 'merged' },
  { label: 'Closed', value: 'closed' },
];

export const PR_REVIEW_TABS: Array<{
  label: string;
  value: Extract<
    FindingsPrView,
    'needs-attention' | 'blocking' | 'advisory' | 'passing'
  >;
  countKey: 'needsAttention' | 'blocking' | 'advisory' | 'passing';
}> = [
  {
    label: 'Needs attention',
    value: 'needs-attention',
    countKey: 'needsAttention',
  },
  { label: 'Blocking', value: 'blocking', countKey: 'blocking' },
  { label: 'Advisory', value: 'advisory', countKey: 'advisory' },
  { label: 'Passing', value: 'passing', countKey: 'passing' },
];

export type PrSummaryLike = {
  totals: {
    findings: number;
    blocking?: number;
    advisory?: number;
    passing?: number;
    safeFixes: number;
    policyNeeded: number;
    manualReview: number;
  };
  review?: {
    approved: number;
    changesRequested: number;
    fixCreated: number;
    commented: number;
    unreviewed: number;
  };
};

export function getPrInboxCounts(rows: IndexedPullRequest[]) {
  return rows.reduce(
    (counts, row) => {
      if (row.mergedAt) counts.merged += 1;
      else if (row.state === 'closed') counts.closed += 1;
      else counts.open += 1;
      return counts;
    },
    { open: 0, merged: 0, closed: 0 },
  );
}

export function filterPrInboxRows(
  rows: IndexedPullRequest[],
  view: PrInboxView,
) {
  return rows.filter((row) => {
    if (view === 'merged') return Boolean(row.mergedAt);
    if (view === 'closed') return row.state === 'closed' && !row.mergedAt;
    return row.state === 'open' && !row.mergedAt;
  });
}

export function getPrInboxStatus(row: IndexedPullRequest): {
  gate: PrGate;
  label: string;
} {
  if (row.mergedAt) return { gate: 'merged', label: 'merged' };
  if (row.state === 'closed') return { gate: 'closed', label: 'closed' };
  if (!row.run) return { gate: 'review', label: 'unscanned' };
  if (row.run.groupCount > 0) return { gate: 'blocked', label: 'blocked' };
  return { gate: 'ready', label: 'ready' };
}

export function getPrCheckDots(row: IndexedPullRequest): PrCheckDot[] {
  if (!row.run) return ['muted', 'muted', 'muted', 'muted'];
  if (row.run.groupCount === 0) return ['pass', 'pass', 'pass', 'pass'];
  const hardStops = Math.min(2, row.run.groupCount);
  return Array.from({ length: 4 }, (_, index) => {
    if (index < hardStops) return 'fail';
    return index === hardStops ? 'warn' : 'pass';
  });
}

export function getPrReviewCounts(summary: PrSummaryLike) {
  const blocking = summary.totals.blocking ?? summary.totals.policyNeeded;
  const advisory =
    summary.totals.advisory ?? Math.max(0, summary.totals.findings - blocking);
  return {
    needsAttention: summary.totals.findings,
    blocking,
    advisory,
    passing: summary.totals.passing ?? 0,
  };
}

export function getPrMergeGate(summary: PrSummaryLike): {
  state: 'blocked' | 'ready';
  title: string;
  meta: string;
} {
  const counts = getPrReviewCounts(summary);
  if (counts.blocking > 0) {
    return {
      state: 'blocked',
      title: `Merge blocked by ${counts.blocking} ${counts.blocking === 1 ? 'finding' : 'findings'}`,
      meta: `${counts.advisory} advisory · ${counts.passing} passing · ${summary.totals.safeFixes} safe ${summary.totals.safeFixes === 1 ? 'fix' : 'fixes'}`,
    };
  }
  return {
    state: 'ready',
    title: 'Ready when review is complete',
    meta: `${summary.totals.findings} findings reviewed · ${counts.passing} passing checks recorded`,
  };
}

export function getPrDecisionGate(row: PrDecisionRow): 'blocking' | 'advisory' {
  if (
    row.actionability === 'blocked' ||
    row.actionability === 'policy_needed'
  ) {
    return 'blocking';
  }
  if (
    row.impact.introduced > 0 &&
    (row.actionability === 'safe_fix' ||
      row.actionability === 'guided_fix' ||
      row.severity === 'critical' ||
      row.severity === 'serious')
  ) {
    return 'blocking';
  }
  return 'advisory';
}

export function normalizePrReviewView(view: FindingsPrView): FindingsPrView {
  if (
    view === 'needs-attention' ||
    view === 'blocking' ||
    view === 'advisory' ||
    view === 'passing'
  ) {
    return view;
  }
  return 'needs-attention';
}
